// PDF page renderer for the Prepare & Send screen.
//
// CONSUMER: internal-ui.
//
// Same pdfjs-dist canvas approach as the email attachment preview
// (web/src/components/email/preview-render-pdf.ts — see its header for
// why canvas beats an <iframe>), with the one addition the prep screen
// needs and the preview deliberately discards: PER-PAGE GEOMETRY. The
// field overlay must convert between on-screen pixels and PDF points,
// so each page returns its scale-1 viewport size (PDF points, top-left
// origin — the coordinate space shared/esign/field-types.ts documents)
// alongside its rendered canvas.
//
// DECISION: a sibling module rather than extending preview-render-pdf,
// so the email preview's contract (an array of canvases, nothing else)
// stays untouched and the two surfaces can tune render scale
// independently. Both lazy-load the same pdfjs chunk, so there is no
// duplicate bundle cost.
//
// pdfjs is imported LAZILY inside renderPdfPages (the pdf-package.ts
// pattern), not at module top level — this module sits in ContactPage's
// static import graph via DocumentsTab → PrepareAndSend, and a
// top-level pdfjs import both drags ~600KB into that graph and crashes
// jsdom render tests (pdfjs touches DOMMatrix at import time; the
// "vitest (fast pools)" failure on this PR's first CI run was exactly
// that).

import {
  pathsFromOperatorList,
  type PdfOpCodes,
  type VectorPathInput,
} from "@shared/esign/vector-blanks";

import { withChunkReload } from "../../lib/lazy-with-reload";

export interface RenderedPdfPage {
  /** 1-based, matching the wire format the send API expects. */
  pageNumber: number;
  canvas: HTMLCanvasElement;
  /** Page size in PDF points (scale-1 viewport) — the space field
   *  coordinates live in. The canvas's CSS size is this × the display
   *  width ratio; its raster size is this × RENDER_SCALE. */
  widthPt: number;
  heightPt: number;
}

/** One run of text from the PDF's text layer, in the SAME space as
 *  everything else here: PDF points, top-left origin, 1-based pages.
 *
 *  Carries an id because the AI pass anchors its answers to spans
 *  ("signature field for the buyer, anchored to span p1s12") rather than
 *  returning coordinates. Geometry is then computed here from the span
 *  we already measured — a model that never emits a number cannot emit
 *  a wrong one. */
export interface PdfTextSpan {
  id: string;
  pageNumber: number;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A real AcroForm widget the PDF already carries. Exact geometry, no
 *  guessing — the cheapest and most accurate rung of the ladder. */
export interface PdfWidget {
  pageNumber: number;
  /** pdf.js field type: "Tx" text, "Btn" button/checkbox, "Sig"
   *  signature, "Ch" choice. Mapped to our vocabulary elsewhere. */
  fieldType: string;
  fieldName: string | null;
  readOnly: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A painted path, flattened to its bounding box in the SAME space as
 *  everything else here: PDF points, top-left origin, 1-based pages.
 *
 *  This is what lets the OS see a blank an AAR form DRAWS rather than
 *  types — see shared/esign/vector-blanks.ts for what is done with it
 *  and why the listing contract needed it. */
export type PdfVectorPath = VectorPathInput;

export interface RenderedPdf {
  numPages: number;
  pages: RenderedPdfPage[];
  /** Empty for a scanned document with no text layer — which is a
   *  supported outcome (manual placement), never an error. */
  spans: PdfTextSpan[];
  widgets: PdfWidget[];
  /** Empty when the page draws nothing (or the operator list couldn't be
   *  read) — a supported outcome, exactly like an absent text layer. */
  vectorPaths: PdfVectorPath[];
}

// Same tradeoff as the email preview: 1.5x is crisp on hi-DPI without
// doubling canvas memory per page.
const RENDER_SCALE = 1.5;

/** Message shown when a PDF can't be rendered at all. Deliberately
 *  plain: the advisor's next move is to use a different copy, not to
 *  decode a parser error. */
export const PDF_UNREADABLE_MESSAGE =
  "This PDF can't be opened for signature preparation — it may be secured, " +
  "digitally sealed, or damaged. Try uploading a fresh copy.";

/** Message shown when the renderer RAN OUT OF TIME rather than failing.
 *  A different sentence from the one above on purpose: "it broke" and
 *  "it never finished" call for different next moves, and telling
 *  someone their file is damaged when it isn't sends them to re-export
 *  a document that was fine. */
/** Suspects THIS PAGE before it suspects the document, because that is
 *  the likelier truth and the cheaper thing to try. Round 5 caught the
 *  old wording telling an advisor their 4.8KB grid PDF "may be unusually
 *  complex" while the real fault was a wedged renderer in the tab — an
 *  honest-looking error pointed at the wrong suspect, which sends people
 *  to re-export a file that was never the problem. */
export const PDF_TIMEOUT_MESSAGE =
  "This document didn't finish opening. That's usually this page rather " +
  "than the file — reloading clears it. If it happens again on the same " +
  "document after a reload, the file itself may be damaged or secured, " +
  "and sending a fresh copy is the next thing to try.";

/** How long each phase gets before we call it. Generous — a real
 *  11-page contract renders in ~18s end to end — but FINITE, which is
 *  the whole point.
 *
 *  Staging round 3 (2026-08-23) found two real forms where pdf.js never
 *  resolved at all: the bytes arrived intact (verified against the
 *  response sizes) and no error was thrown, so the screen sat on
 *  "Loading…" forever with no spinner progress, no message and no
 *  retry. A spinner that cannot fail teaches advisors the tool is
 *  unreliable even when it usually works, so the renderer now gives up
 *  and says so. The underlying reason those two documents hang is still
 *  unknown and needs the files plus a debugger — this makes the symptom
 *  survivable, and names the phase so the next investigation starts
 *  with a fact instead of a guess. */
const OPEN_TIMEOUT_MS = 45_000;
const PAGE_TIMEOUT_MS = 20_000;

/** Rejects if `work` hasn't settled in time. The phase name rides along
 *  on the error so a timeout report says WHERE it died. */
function withTimeout<T>(work: Promise<T>, ms: number, phase: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new PdfRenderTimeout(phase));
    }, ms);
    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

export class PdfRenderTimeout extends Error {
  readonly phase: string;
  constructor(phase: string) {
    super(PDF_TIMEOUT_MESSAGE);
    this.name = "PdfRenderTimeout";
    this.phase = phase;
  }
}

// pdf.js reports both text items and annotation rectangles in PDF user
// space — bottom-left origin, y UP. Everything downstream of this module
// is top-left origin, y DOWN (shared/esign/field-types.ts). These two
// readers are where that flip happens for the suggestion inputs, exactly
// as the scale-1 viewport does it for page size: ONE conversion, stated
// once, so no later stage has to wonder which way up a number is.

/** Text runs with geometry. `str` items only — pdf.js also emits
 *  `marked content` items with no text, which carry no position. */
async function readTextSpans(
  page: { getTextContent(): Promise<{ items: unknown[] }> },
  pageHeightPt: number,
  pageNumber: number,
): Promise<PdfTextSpan[]> {
  const content = await page.getTextContent();
  const out: PdfTextSpan[] = [];
  let index = 0;
  for (const raw of content.items) {
    const item = raw as {
      str?: unknown;
      transform?: unknown;
      width?: unknown;
      height?: unknown;
    };
    const text = typeof item.str === "string" ? item.str : "";
    // Whitespace-only runs are noise for every consumer here — the
    // heuristics look for labels and underscore runs, and the AI pass
    // pays per token for anything we send it.
    if (!text.trim()) continue;
    const transform = item.transform;
    if (!Array.isArray(transform) || transform.length < 6) continue;
    const x = Number(transform[4]);
    const yBottom = Number(transform[5]);
    const width = Number(item.width ?? 0);
    const height = Number(item.height ?? 0) || Math.abs(Number(transform[3])) || 0;
    if (!Number.isFinite(x) || !Number.isFinite(yBottom)) continue;
    out.push({
      id: `p${pageNumber}s${index++}`,
      pageNumber,
      text,
      x,
      // transform[5] is the text BASELINE measured from the page bottom.
      // Top-left y of the run's box = pageHeight − (baseline + height).
      y: pageHeightPt - (yBottom + height),
      width,
      height,
    });
  }
  return out;
}

/** AcroForm widgets — the fields the PDF already declares. */
async function readWidgets(
  page: { getAnnotations(): Promise<unknown[]> },
  pageHeightPt: number,
  pageNumber: number,
): Promise<PdfWidget[]> {
  const annotations = await page.getAnnotations();
  const out: PdfWidget[] = [];
  for (const raw of annotations) {
    const a = raw as {
      subtype?: unknown;
      fieldType?: unknown;
      fieldName?: unknown;
      readOnly?: unknown;
      hidden?: unknown;
      rect?: unknown;
    };
    if (a.subtype !== "Widget") continue;
    // A hidden widget is not a place a person can sign.
    if (a.hidden === true) continue;
    const rect = a.rect;
    if (!Array.isArray(rect) || rect.length < 4) continue;
    const [x1, y1, x2, y2] = rect.map(Number);
    if (![x1, y1, x2, y2].every((n) => Number.isFinite(n))) continue;
    const left = Math.min(x1!, x2!);
    const right = Math.max(x1!, x2!);
    const bottom = Math.min(y1!, y2!);
    const top = Math.max(y1!, y2!);
    out.push({
      pageNumber,
      fieldType: typeof a.fieldType === "string" ? a.fieldType : "",
      fieldName: typeof a.fieldName === "string" ? a.fieldName : null,
      readOnly: a.readOnly === true,
      x: left,
      y: pageHeightPt - top,
      width: right - left,
      height: top - bottom,
    });
  }
  return out;
}

/**
 * The paths a page PAINTS — the drawn ruled blanks and checkbox squares
 * that carry no text and no form field.
 *
 * WHY THIS EXISTS (Nate, 2026-09-08): AAR forms draw every blank as a
 * vector rule instead of typing underscores, so the whole text-layer
 * rung of the suggestion ladder was blind to them. The listing contract
 * has ~75 blanks and the OS found seven. The lines were there the whole
 * time, in the page's own drawing instructions.
 *
 * The WALK itself lives in shared/esign/vector-blanks.ts and takes
 * pdf.js's opcode numbers as an argument, so the same code that runs in
 * the browser is exercised by a node test against a real PDF. This
 * function is only the plumbing: fetch the operator list, hand over the
 * page's scale-1 transform (which carries the top-left flip and any
 * rotation, so paths land in the same space as spans and widgets).
 *
 * Best-effort, like the text and widget readers: a page whose operator
 * list can't be read simply contributes nothing.
 */
async function readVectorPaths(
  page: { getOperatorList(): Promise<{ fnArray: unknown; argsArray: unknown }> },
  pdfjsLib: typeof import("pdfjs-dist"),
  viewportTransform: number[],
  pageNumber: number,
): Promise<PdfVectorPath[]> {
  const opList = await page.getOperatorList();
  return pathsFromOperatorList(opList, pdfjsLib.OPS as unknown as PdfOpCodes, {
    viewportTransform,
    pageNumber,
  });
}

/** ONE pdf.js worker for the whole session, reused across opens.
 *
 *  pdf.js starts a fresh background worker for every `getDocument` call
 *  that isn't handed one, and `loadingTask.destroy()` is what shuts that
 *  worker down. Since this module never destroyed a SUCCESSFUL load (see
 *  the finally below), every prepared document left a worker thread
 *  running, holding its parsed document alive. Staging round 5
 *  (2026-08-25) is what this cost: the first prep of a session rendered
 *  in ~9s, and after one send EVERY later prep timed out — same 5KB
 *  file, fresh tabs included, because same-origin tabs share the
 *  browser's renderer process and therefore the starved thread pool.
 *  The advisor's honest reading of that is "the feature stopped
 *  working."
 *
 *  Reused rather than merely destroyed because worker startup re-parses
 *  ~600KB of pdf.js on every open; sharing makes the second document
 *  faster than the first instead of slower. */
let sharedWorker: import("pdfjs-dist").PDFWorker | null = null;

async function acquireWorker(
  pdfjsLib: typeof import("pdfjs-dist"),
): Promise<import("pdfjs-dist").PDFWorker | null> {
  try {
    // A worker that died (crashed tab, an earlier fatal parse) reports
    // itself destroyed — never hand that one out again.
    if (sharedWorker && !sharedWorker.destroyed) return sharedWorker;
    sharedWorker = new pdfjsLib.PDFWorker();
    return sharedWorker;
  } catch {
    // Worker construction is not load-bearing: null means "let pdf.js
    // make its own", which is exactly the pre-2026-08-25 behavior. The
    // leak fix below stands on its own either way.
    sharedWorker = null;
    return null;
  }
}

export async function renderPdfPages(bytes: ArrayBuffer): Promise<RenderedPdf> {
  // Wrapped so a deploy that landed while this tab was open — the
  // pdfjs chunk got a new hash and this one 404s — reloads the page
  // once instead of surfacing "Failed to fetch dynamically imported
  // module" to whoever is trying to open or map a document.
  const pdfjsLib = await withChunkReload(() => import("pdfjs-dist"));
  const workerUrl = (
    await withChunkReload(() => import("pdfjs-dist/build/pdf.worker.min.mjs?url"))
  ).default;
  pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
  const data = new Uint8Array(bytes);
  const worker = await acquireWorker(pdfjsLib);
  // Passing our own worker also means loadingTask.destroy() tears down
  // only THIS document's transport and leaves the shared worker alive —
  // pdf.js destroys a worker only when it created it itself.
  const loadingTask = pdfjsLib.getDocument(worker ? { data, worker } : { data });
  // Everything from here is wrapped so a PDF pdf.js can't parse becomes
  // a message on screen instead of an unhandled promise rejection.
  //
  // Sentry caught the real case (production, 2026-08-23): "TypeError:
  // Junk found after end of compressed data" when the prep screen was
  // opened on a BoldSign-SEALED PDF — a digitally signed document whose
  // compressed streams pdf.js refuses. The entry point to that specific
  // case is now closed (a fully_signed document no longer offers "Send
  // for signature"), but any secured or damaged upload can do the same,
  // so the renderer itself has to fail gracefully rather than rely on
  // nobody reaching it.
  try {
    const doc = await withTimeout(loadingTask.promise, OPEN_TIMEOUT_MS, "open");
    const pages: RenderedPdfPage[] = [];
    const spans: PdfTextSpan[] = [];
    const widgets: PdfWidget[] = [];
    const vectorPaths: PdfVectorPath[] = [];
    for (let i = 1; i <= doc.numPages; i++) {
      const page = await withTimeout(doc.getPage(i), PAGE_TIMEOUT_MS, `getPage:${i}`);
      // Scale-1 viewport carries the page's size in PDF points with the
      // top-left-origin flip already applied — the canonical geometry the
      // overlay and the send API share.
      const base = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: RENDER_SCALE });
      const canvas = document.createElement("canvas");
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const context = canvas.getContext("2d");
      if (!context) {
        throw new Error("Failed to acquire 2D canvas context");
      }
      await withTimeout(
        page.render({ canvasContext: context, viewport, canvas }).promise,
        PAGE_TIMEOUT_MS,
        `render:${i}`,
      );
      pages.push({ pageNumber: i, canvas, widthPt: base.width, heightPt: base.height });

      // Field-suggestion inputs, gathered while the page is already
      // parsed and in memory. Each is independently best-effort: a
      // document with no text layer (a scan) or no form is a supported
      // outcome — it degrades to manual placement, never to an error,
      // so neither may take the successful render down with it.
      try {
        spans.push(...(await readTextSpans(page, base.height, i)));
      } catch {
        // No text layer on this page. Manual placement still works.
      }
      try {
        widgets.push(...(await readWidgets(page, base.height, i)));
      } catch {
        // No annotations on this page.
      }
      try {
        // The operator list is a SECOND parse of the page's content
        // stream (pdf.js keeps rendering and op-list intents separate),
        // which is the cost of seeing drawn blanks at all. Bounded by
        // the same per-page timeout as everything else, and best-effort:
        // a page that times out here still renders, still shows its
        // text-layer proposals, and simply contributes no drawn blanks.
        vectorPaths.push(
          ...(await withTimeout(
            readVectorPaths(page, pdfjsLib, base.transform, i),
            PAGE_TIMEOUT_MS,
            `paths:${i}`,
          )),
        );
      } catch {
        // No readable operator list on this page.
      }
    }
    return { numPages: doc.numPages, pages, spans, widgets, vectorPaths };
  } catch (err) {
    // A timeout keeps its own message and phase — it is a different
    // fact from "this file is broken" and must not be flattened into
    // one. Everything else is genuinely unreadable.
    if (err instanceof PdfRenderTimeout) throw err;
    throw new Error(PDF_UNREADABLE_MESSAGE, { cause: err });
  } finally {
    // ALWAYS, success included. The failure path already did this —
    // dangling on an error made pdf.js reject internal promises with
    // nothing attached, surfacing as an unhandled rejection despite the
    // caller's try/catch — but a successful load was left holding its
    // document and worker connection open forever, which is the
    // degradation described on sharedWorker above.
    //
    // Safe here: the canvases are painted (independent DOM nodes) and
    // the spans and widgets are plain objects, so nothing returned
    // still needs the document.
    try {
      await loadingTask.destroy();
    } catch {
      // Already dead; nothing to release.
    }
  }
}
